import { useEffect, useMemo, useState } from 'react';
import { Box, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import { Reorder } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { RosterMember } from '@/model';
import { RosterMemberRow } from './RosterMemberRow';

const PREFIX = 'pages.application_roster_member.components.roster_member_table';

const HEADER_KEYS = [
  'roster_member_table_type',
  'roster_member_table_name',
  'roster_member_table_role_label',
  'roster_member_table_group',
  'roster_member_table_shirt_number',
  'roster_member_table_created_at',
  'roster_member_table_actions',
] as const;

export interface RosterMemberTableProps {
  data: RosterMember[];
  isLoading: boolean;
  isReorderInFlight?: boolean;
  onEditClick: (item: RosterMember) => void;
  onDeleteClick: (item: RosterMember) => void;
  onReorder: (orderedUuids: string[]) => void;
}

export const RosterMemberTable = ({
  data,
  isLoading,
  isReorderInFlight = false,
  onEditClick,
  onDeleteClick,
  onReorder,
}: RosterMemberTableProps) => {
  const { t } = useTranslation();

  const [order, setOrder] = useState<string[]>(() => data.map(item => item.uuid));

  useEffect(() => {
    setOrder(data.map(item => item.uuid));
  }, [data]);

  const itemsByUuid = useMemo(() => {
    const map = new Map<string, RosterMember>();
    data.forEach(item => map.set(item.uuid, item));
    return map;
  }, [data]);

  const orderedItems = useMemo(
    () => order.map(uuid => itemsByUuid.get(uuid)).filter((item): item is RosterMember => !!item),
    [order, itemsByUuid],
  );

  const handleReorder = (nextIds: string[]) => {
    if (isReorderInFlight) return;
    setOrder(nextIds);

    let same = nextIds.length === order.length;
    if (same) {
      for (let i = 0; i < nextIds.length; i += 1) {
        if (nextIds[i] !== order[i]) {
          same = false;
          break;
        }
      }
    }
    if (same) return;
    onReorder(nextIds);
  };

  return (
    <Box borderWidth='1px' borderColor='gray.100' borderRadius='md' overflowX='auto' bg='white'>
      <Text fontSize='xs' color='gray.500' px={4} pt={3} pb={2}>
        {t(`${PREFIX}.roster_member_table_reorder_hint`)}
      </Text>

      <Table size='sm' variant='simple'>
        <Thead sx={{ backgroundColor: 'var(--chakra-colors-gray-100) !important' }}>
          <Tr>
            <Th px={2} w='32px' sx={{ backgroundColor: 'var(--chakra-colors-gray-100) !important' }} />
            {HEADER_KEYS.map(key => (
              <Th
                key={key}
                px={key === 'roster_member_table_actions' ? 2 : 3}
                textAlign={key === 'roster_member_table_actions' ? 'right' : 'left'}
                sx={{
                  color: 'var(--chakra-colors-gray-800) !important',
                  backgroundColor: 'var(--chakra-colors-gray-100) !important',
                }}
              >
                {t(`${PREFIX}.${key}`)}
              </Th>
            ))}
          </Tr>
        </Thead>

        <Reorder.Group as='tbody' axis='y' values={order} onReorder={handleReorder}>
          {orderedItems.map(item => (
            <RosterMemberRow
              key={item.uuid}
              item={item}
              isDragDisabled={isReorderInFlight}
              onEditClick={onEditClick}
              onDeleteClick={onDeleteClick}
            />
          ))}
        </Reorder.Group>

        {!isLoading && orderedItems.length === 0 ? (
          <Tbody>
            <Tr>
              <Td colSpan={HEADER_KEYS.length + 1} textAlign='center' py={6} color='gray.500'>
                {t(`${PREFIX}.roster_member_table_empty`)}
              </Td>
            </Tr>
          </Tbody>
        ) : null}
      </Table>
    </Box>
  );
};
